// <pos-checkout-result/> — 金流結果頁（LINE Pay confirm / 綠界 OrderResultURL redirect 落點）
// see architecture.md#L280（金流架構策略 v2.0）
//
// 路由：#/checkout/result/:status?order_id=xxx
//   status ∈ success | failed | pending | cancelled | error
//
// 安全前提：本頁純 UI 展示，**不是**付款成功的依據 —— 訂單真正轉 paid
// 只發生在 server 端驗章（payment-return.js / payment-linepay-confirm.js）。
// 即使有人手動輸入 /result/success 也不會改變任何訂單狀態。

import { hashQuery } from '../pos/routes.js';
import { t } from '../lib/i18n.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

const STATES = {
  success: {
    icon: '✅',
    get title() { return t('付款完成'); },
    get desc() { return t('我們已收到您的付款，餐點準備中。'); },
    tone: 'ok',
  },
  pending: {
    icon: '⏳',
    get title() { return t('付款確認中'); },
    get desc() { return t('金流端尚未回覆最終結果，請稍候並重新整理；若超過 5 分鐘仍未完成，請洽店員。'); },
    tone: 'warn',
  },
  failed: {
    icon: '❌',
    get title() { return t('付款失敗'); },
    get desc() { return t('這筆付款沒有成功，您沒有被扣款。請回到結帳頁重試，或改用其他付款方式。'); },
    tone: 'err',
  },
  cancelled: {
    icon: '↩️',
    get title() { return t('已取消付款'); },
    get desc() { return t('您已取消這次付款，訂單尚未付款。'); },
    tone: 'warn',
  },
  error: {
    icon: '⚠️',
    get title() { return t('發生錯誤'); },
    get desc() { return t('付款流程發生異常，若已扣款請保留畫面並洽店員核對。'); },
    tone: 'err',
  },
};

class PosCheckoutResult extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
    // redirect 帶 query 的 hash 變動（同 component 不 remount）也要重渲染
    this._onHash = () => this.render();
    window.addEventListener('hashchange', this._onHash);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this._onHash);
  }

  parse() {
    const hash = location.hash || '';
    const path = hash.split('?')[0];
    const status = path.split('/')[3] || 'error';   // #/checkout/result/:status
    const q = hashQuery(hash);
    return {
      state: STATES[status] || STATES.error,
      statusKey: STATES[status] ? status : 'error',
      orderId: q.get('order_id') || '',
      code: q.get('code') || '',
    };
  }

  render() {
    const { state, statusKey, orderId, code } = this.parse();
    // 員工裝置（已登入）顯示「回訂單清單」；客人手機顯示「完成」即可
    const authed = document.body.dataset.authed === 'true';
    const shortId = orderId ? orderId.slice(0, 8) : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-6, 32px) var(--s-5, 24px); max-width: 480px; margin: 0 auto; font-family: var(--font-sans, sans-serif); color: var(--fg-base, #222); text-align: center; }
        .icon { font-size: 56px; line-height: 1; margin-bottom: var(--s-4, 16px); }
        h1 { font-size: 1.35rem; margin: 0 0 var(--s-3, 12px); }
        .desc { color: var(--fg-muted, #666); font-size: .95rem; line-height: 1.6; margin-bottom: var(--s-5, 24px); }
        .meta { font-size: .8rem; color: var(--fg-dim, #999); margin-bottom: var(--s-5, 24px); }
        .meta code { background: var(--bg-elevated, #f4f4f4); padding: 2px 8px; border-radius: 4px; }
        .card { padding: var(--s-5, 24px); border-radius: var(--r-lg, 12px); border: 1px solid var(--border, #e0e0e0); background: var(--bg-elevated, #fff); }
        .card[data-tone="ok"]   { border-color: var(--success-fg, #155724); }
        .card[data-tone="err"]  { border-color: var(--danger-fg, #721c24); }
        .card[data-tone="warn"] { border-color: var(--warning-fg, #856404); }
        .actions { display: flex; gap: var(--s-3, 12px); justify-content: center; margin-top: var(--s-4, 16px); }
        .btn {
          min-height: var(--touch-cta, 48px); padding: 0 var(--s-5, 24px);
          border-radius: var(--r-md, 8px); font-size: 1rem; font-weight: 600;
          cursor: pointer; font-family: inherit; border: 0;
          background: var(--brand-primary, #e8580c); color: var(--fg-on-brand, #fff);
        }
        .btn--ghost { background: transparent; color: var(--fg-base, #222); border: 2px solid var(--border, #e0e0e0); }
      </style>
      <div class="card" data-tone="${state.tone}" data-testid="checkout-result" data-status="${statusKey}">
        <div class="icon">${state.icon}</div>
        <h1>${state.title}</h1>
        <div class="desc">${state.desc}</div>
        ${shortId ? `<div class="meta">${t('訂單編號')} <code data-testid="result-order-id">${escapeHtml(shortId)}</code>${code ? `（${t('代碼')} ${escapeHtml(code)}）` : ''}</div>` : ''}
        <div class="actions">
          ${authed
            ? `<button class="btn" data-testid="result-back-orders" data-nav="#/orders">${t('回訂單清單')}</button>
               ${statusKey === 'failed' || statusKey === 'cancelled'
                 ? `<button class="btn btn--ghost" data-testid="result-retry" data-nav="#/checkout">${t('重新結帳')}</button>` : ''}`
            : `<button class="btn btn--ghost" data-testid="result-close" data-close>${t('完成')}</button>`}
        </div>
      </div>
    `;

    this.shadowRoot.querySelectorAll('[data-nav]').forEach((btn) => {
      btn.addEventListener('click', () => { location.hash = btn.dataset.nav; });
    });
    this.shadowRoot.querySelector('[data-close]')?.addEventListener('click', () => {
      // 客人端：嘗試關頁（LINE 內建瀏覽器可關）；不行就回根
      window.close();
      setTimeout(() => { location.hash = '#/'; }, 200);
    });
  }
}

customElements.define('pos-checkout-result', PosCheckoutResult);
